import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useCompanyData,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { PlusIcon, ProceedIcon } from '@commenda/icons';
import {
  Button,
  Loading,
  LoadingIndicator,
  MultiSelect,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  DirectorPersonalDetails,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { DirectorCardKS, IncorporationFormHeading } from '../../common';
import { getDirectorName } from '../../Director/common';
import DirectorModal from '../DirectorModal';
import DirectorForm from './DirectorForm';
import { validateDirectorInfo } from './validators';

const DirectorInfo = ({
  next,
  previous,
  data,
  mutate,
  corporationId,
  token,
  companyId,
  setNotification,
  resetFurtherSteps,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  corporationId: number;
  token: string;
  companyId: number;
  setNotification: TSetNotification;
  resetFurtherSteps: () => void;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const [directors, setDirectors] = useState<DirectorBase[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const {
    data: companyData,
    error,
    mutate: companyMutate,
  } = useGetAllDirectorsInCompany(companyId);

  // Get corporations data for cross-entity email validation
  const { corporations } = useCompanyData(companyId);

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const selectedDirectorIdsRef = useRef<number[]>([]);
  const [preAssignedDirectorId, setPreAssignedDirectorId] = useState(null);

  useEffect(() => {
    setDirectors(
      companyData?.directors?.map((eachDirector) => ({
        id: eachDirector?.id,
        directorFirstName: eachDirector.directorFirstName,
        directorLastName: eachDirector.directorLastName,
        email: eachDirector.email,
        operatingAddress: eachDirector.operatingAddress,
        directorIdentificationNumber: eachDirector.directorIdentificationNumber,
        personalDetails: {
          nationality: (eachDirector.personalDetails as DirectorPersonalDetails)
            ?.nationality as Country,
          residence: (eachDirector.personalDetails as DirectorPersonalDetails)
            ?.residence as Country,
          educationalQualification:
            eachDirector?.personalDetails?.educationalQualification,
          hasDSC: eachDirector?.personalDetails?.hasDSC,
          occupation: eachDirector?.personalDetails?.occupation,
          placeOfBirth: eachDirector?.personalDetails?.placeOfBirth,
        },
        directorFiles: eachDirector.directorFiles,
        number: eachDirector.number,
        corporationHasDirector: eachDirector.corporationHasDirector,
        governmentId: eachDirector?.governmentId,
        addressProof: eachDirector?.addressProof,
        aadharCard: eachDirector?.aadharCard,
        panCard: eachDirector?.panCard,
      }))
    );
  }, [companyData]);

  useEffect(() => {
    const alreadySelectedDirectors = [...selectedDirectorIdsRef.current];

    companyData?.directors
      ?.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation?.isDirector
        )
      )
      .forEach((director) => {
        if (director?.WorkRequests?.find((wrkReq) => wrkReq.id === id)) {
          if (!alreadySelectedDirectors.includes(director.id))
            alreadySelectedDirectors.push(director.id);
        }
      });

    if (data?.primaryAssociatedDirectorId)
      setPreAssignedDirectorId(data.primaryAssociatedDirectorId);

    setSelectedDirectorIds(alreadySelectedDirectors);
  }, [companyData]);

  if (!companyData) {
    if (error) {
      console.error(error);
    }
    return <Loading />;
  }

  const onSubmit = async () => {
    setSubmitting(true);

    if (!data?.id) {
      setNotification({
        show: true,
        valid: false,
        message: 'Work request ID is missing. Please refresh the page.',
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    if (selectedDirectorIds.length < 2) {
      setNotification({
        show: true,
        valid: false,
        message: `Indian LLP requires you to have at least two partners.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    // Validate email uniqueness
    const companyEmail = data?.requestData?.contactEmail;
    const selectedDirectors = directors?.filter((director) =>
      selectedDirectorIds.includes(director.id)
    );
    const directorEmails =
      selectedDirectors
        ?.map((director) => director.email?.trim()?.toLowerCase())
        .filter(Boolean) || [];

    if (companyEmail) {
      const allEmails = [companyEmail.trim().toLowerCase(), ...directorEmails];
      const duplicateEmails = allEmails.filter(
        (email, index) => allEmails.indexOf(email) !== index
      );

      if (duplicateEmails.length > 0) {
        setNotification({
          show: true,
          valid: false,
          message:
            'Email addresses of the company and the directors must be unique.',
          title: 'Error',
        });
        setSubmitting(false);
        return;
      }
    }

    const formError = validateDirectorInfo(
      directors,
      selectedDirectorIds,
      corporationId
    );

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));
      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for partners by clicking on "Edit".`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const directorsToDisconnect = companyData?.directors
      ?.filter((eachDirector) =>
        eachDirector?.corporationHasDirector?.find(
          (dirWithCorp) =>
            dirWithCorp.corporationId === data.corporationId &&
            dirWithCorp.additionalDirectorInformation?.isDirector &&
            !selectedDirectorIds.includes(dirWithCorp.directorId)
        )
      )
      ?.map((each) => each.id);

    const directorUpdates: UpdateMultipleDirectorInputType[] = [
      ...selectedDirectorIds.map((eachDirId) => ({
        id: eachDirId,
        wrkReqId: data.id,
        corporationHasDirector: [
          {
            corporationId: data.corporationId,
            directorId: eachDirId,
            additionalDirectorInformation: {
              isDirector: true,
            },
          },
        ],
      })),
      ...directorsToDisconnect.map((eachDirId) => ({
        id: eachDirId,
        workRequestIdToDisconnect: data.id,
        corporationIdsToDisconnect: [data.corporationId],
      })),
    ];

    const response = await updateMultipleDirectors(directorUpdates, token);

    if (!response) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      setSubmitting(false);
      return;
    }

    await mutate();
    next();
    setSubmitting(false);
  };

  const selectDirectorById = (directorId: number) => {
    if (selectedDirectorIds.includes(directorId)) return;

    setSelectedDirectorIds((prevDirectorIds) => {
      return [...prevDirectorIds, directorId];
    });
  };

  const hasDirectors = companyData?.directors?.length;

  return (
    <div className="flex flex-col gap-6 max-w-[570px]">
      <IncorporationFormHeading
        text="Add designated partners"
        additionalText="Please provide information about this corporation's designated partners."
      />

      <div className="flex flex-col gap-2">
        {hasDirectors ? (
          <MultiSelect
            options={(companyData?.directors || []).map((dir) => ({
              label: getDirectorName(dir),
              value: dir.id.toString(),
            }))}
            onValueChange={(val: string[]) => {
              setSelectedDirectorIds(val.map(Number));
              selectedDirectorIdsRef.current = val.map(Number);
              resetFurtherSteps();
            }}
            defaultValue={selectedDirectorIds.map(String)}
            hideSelectAll
            addItemConfig={{
              shouldAllowAdd: true,
              onAddItem: () => setDirectorModalShown(true),
              placeholder: 'Add new designated partner',
            }}
            placeholder="Add or select a designated partner"
            hideTags
          />
        ) : (
          <Button
            variant="secondary"
            className="w-full flex gap-2 items-center justify-center"
            onClick={() => setDirectorModalShown(true)}
          >
            <PlusIcon className="text-neutral-1" />
            <span>Add designated partners</span>
          </Button>
        )}
        <span className="text-text-tertiary">
          At least two designated partners are required*
        </span>
      </div>

      <div>
        {directors
          ?.filter((dir) => selectedDirectorIds.includes(dir.id))
          .map((director) => {
            return (
              <DirectorCardKS
                key={director.id}
                director={director}
                companyDataMutate={companyMutate}
                setNotification={setNotification}
                corporationId={corporationId}
                companyId={companyId}
                token={token}
                hasError={directorIdsWithError.has(director.id)}
                directorForm={DirectorForm}
                onErrorResolve={() =>
                  setDirectorIdsWithError((prevIds) => {
                    const newIds = new Set(prevIds);
                    newIds.delete(director.id);
                    return newIds;
                  })
                }
                onRemoveDirector={async (directorId: number) => {
                  setSelectedDirectorIds((prev) =>
                    prev.filter((dirId) => dirId !== director.id)
                  );
                  selectedDirectorIdsRef.current = selectedDirectorIds;
                  resetFurtherSteps();
                }}
                allCorporations={corporations}
                workRequestData={data}
              />
            );
          })}
      </div>

      <div className="w-[600px]">
        <DirectorModal
          isOpen={isDirectorModalShown}
          setOpen={setDirectorModalShown}
          companyDataMutate={companyMutate}
          DirectorForm={DirectorForm}
          corporationId={data.corporationId}
          selectDirectorById={selectDirectorById}
          companyId={companyId}
          setNotification={setNotification}
          token={token}
          setSelectedDirectorIds={setSelectedDirectorIds}
          selectedDirectorIds={selectedDirectorIds}
          modalConfig={{
            isDirector: true,
            postSave: (dir) => {
              if (!selectedDirectorIdsRef.current.includes(dir.id)) {
                selectedDirectorIdsRef.current.push(dir.id);
              }
            },
          }}
          workRequestData={data}
          allCorporations={corporations}
        />
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || directors?.length === 0}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span className={classNames(isSubmitting ? 'ml-2' : '')}>
            Continue
          </span>
          {!isSubmitting && <ProceedIcon className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default DirectorInfo;
